import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/CustomerApiNew';
import CreateTenderModal from './CreateTenderModal';
import './CustomerDashboard.css';

const API_BASE_URL = '/api/v1';

const CustomerDashboard = () => {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('tenders');
	const [profile, setProfile] = useState(null);
	const [tenders, setTenders] = useState([]);
	const [selectedTender, setSelectedTender] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [loaderData, setLoaderData] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [extendDate, setExtendDate] = useState('');
	const [loading, setLoading] = useState(true);
	const [profileForm, setProfileForm] = useState({ name: '', city: '', state: '', email: '' });
	const [previewPhotos, setPreviewPhotos] = useState(null);
	const [previewIndex, setPreviewIndex] = useState(0);

	const fetchProfile = async () => {
		const res = await api.get(`${API_BASE_URL}/customer/me`);
		setProfile(res.data);
		setProfileForm({
			name: res.data.name || '',
			city: res.data.city || '',
			state: res.data.state || '',
			email: res.data.email || ''
		});
	};

	const fetchTenders = async () => {
		const res = await api.get(`${API_BASE_URL}/tenders/my`);
		setTenders(res.data);
	};

	const fetchLoader = async () => {
		const res = await api.get(`${API_BASE_URL}/loader/load`);
		setLoaderData(res.data);
	};

	useEffect(() => {
		const init = async () => {
			try {
				await Promise.all([fetchProfile(), fetchTenders(), fetchLoader()]);
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		};
		init();
	}, []);

	useEffect(() => {
		if (!previewPhotos) return undefined;
		const onKeyDown = (e) => {
			if (e.key === 'Escape') setPreviewPhotos(null);
			if (e.key === 'ArrowLeft') {
				setPreviewIndex(i => (i > 0 ? i - 1 : previewPhotos.length - 1));
			}
			if (e.key === 'ArrowRight') {
				setPreviewIndex(i => (i < previewPhotos.length - 1 ? i + 1 : 0));
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [previewPhotos]);

	const loadParticipants = async (tender) => {
		setSelectedTender(tender);
		setExtendDate('');
		try {
			const res = await api.get(`${API_BASE_URL}/tenders/${tender.id}/participants`);
			setParticipants(res.data);
		} catch (err) {
			setParticipants([]);
		}
		requestAnimationFrame(() => {
			document.getElementById('cd-tender-detail-panel')?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest'
			});
		});
	};

	const handleAssign = async (bidId, e) => {
		e.stopPropagation();
		if (!selectedTender) return;
		if (!window.confirm('Assign this tender to this participant? The tender will be closed.')) return;
		try {
			const res = await api.put(`${API_BASE_URL}/tenders/${selectedTender.id}/assign/${bidId}`);
			setSelectedTender(res.data);
			await fetchTenders();
			await loadParticipants(res.data);
			alert('Tender assigned successfully');
		} catch (err) {
			alert(err.response?.data?.message || 'Failed to assign tender');
		}
	};

	const handleParticipantClick = (providerId) => {
		navigate(`/provider/${providerId}`);
	};

	const handleExtendTender = async () => {
		if (!selectedTender || !extendDate) return;
		try {
			await api.put(`${API_BASE_URL}/tenders/${selectedTender.id}/extend`, { newEndDate: extendDate });
			await fetchTenders();
			setExtendDate('');
			alert('Tender end date extended successfully');
		} catch (err) {
			alert(err.response?.data?.message || 'Failed to extend tender');
		}
	};

	const handleProfileUpdate = async (e) => {
		e.preventDefault();
		try {
			const res = await api.put(`${API_BASE_URL}/customer/me`, profileForm);
			setProfile(res.data);
			alert('Profile updated successfully');
		} catch (err) {
			alert('Failed to update profile');
		}
	};

	const handleLogout = () => {
		localStorage.removeItem('customerAccessToken');
		localStorage.removeItem('customerRefreshToken');
		navigate('/');
	};

	if (loading) {
		return <div className="customer-dashboard"><div className="loader-center">Loading...</div></div>;
	}

	return (
		<div className="customer-dashboard">
			<header className="cd-header">
				<Link to="/" className="cd-logo">MyBharatService</Link>
				<div className="cd-header-actions">
					<button className="btn-create" onClick={() => setShowCreateModal(true)}>+ Create Tender</button>
					<button className="btn-logout" onClick={handleLogout}>Logout</button>
				</div>
			</header>

			<div className="cd-layout">
				<aside className="cd-sidebar">
					<button className={activeTab === 'tenders' ? 'active' : ''} onClick={() => setActiveTab('tenders')}>
						My Tenders
					</button>
					<button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
						My Profile
					</button>
				</aside>

				<main className="cd-main">
					{activeTab === 'tenders' && (
						<div className="cd-tenders-view">
							<h2>My Tenders</h2>
							<div className="cd-tender-list">
								{tenders.length === 0 && <p className="empty-msg">No tenders yet. Create your first tender!</p>}
								{tenders.map(t => {
									const isSelected = selectedTender?.id === t.id;
									return (
										<div key={t.id} className="cd-tender-item">
											<div
												className={`tender-card ${isSelected ? 'selected' : ''}`}
												onClick={() => loadParticipants(t)}
											>
												<div className="tender-card-header">
													<strong>{t.serviceType}</strong>
													<span className={`status-badge ${t.status?.toLowerCase()}`}>{t.status}</span>
												</div>
												<p className="tender-meta">{t.city}{t.area ? ` · ${t.area}` : ''} | {t.bidCount} bid(s)</p>
												<p className="tender-dates">{t.startDate} → {t.endDate}</p>
												<p className="tender-desc">{t.description?.substring(0, 80)}...</p>
											</div>

											{isSelected && (
												<div id="cd-tender-detail-panel" className="cd-tender-detail cd-tender-detail-inline">
													<h3>Tender Details</h3>
													<div className="detail-block">
														<p><strong>Service:</strong> {selectedTender.serviceType}</p>
														{selectedTender.serviceSubType && <p><strong>Specialization:</strong> {selectedTender.serviceSubType}</p>}
														{selectedTender.propertyType && <p><strong>Property:</strong> {selectedTender.propertyType}</p>}
														{selectedTender.totalAreaSqft && <p><strong>Area:</strong> {selectedTender.totalAreaSqft} SQFT</p>}
														{selectedTender.numberOfFloors && <p><strong>Floors:</strong> {selectedTender.numberOfFloors}</p>}
														<p><strong>City:</strong> {selectedTender.city}</p>
														{selectedTender.area && <p><strong>Locality:</strong> {selectedTender.area}</p>}
														<p><strong>Description:</strong> {selectedTender.description}</p>
														{selectedTender.bookingDate && <p><strong>Booking Date:</strong> {selectedTender.bookingDate} </p>}
														<p><strong>Tender Period:</strong> {selectedTender.startDate} to {selectedTender.endDate}</p>
														{Array.isArray(selectedTender.photoUrls) && selectedTender.photoUrls.length > 0 && (
															<div className="tender-ref-photos">
																<p><strong>Reference photos</strong></p>
																<div className="tender-ref-photos-grid">
																	{selectedTender.photoUrls.map((url, idx) => (
																		<button
																			key={`${url}-${idx}`}
																			type="button"
																			className="tender-ref-photo-thumb"
																			onClick={() => {
																				setPreviewPhotos(selectedTender.photoUrls);
																				setPreviewIndex(idx);
																			}}
																		>
																			<img src={url} alt={`Reference ${idx + 1}`} loading="lazy" />
																		</button>
																	))}
																</div>
															</div>
														)}
													</div>

													{selectedTender.status === 'OPEN' && (
														<div className="extend-section">
															<h4>Extend End Date</h4>
															<div className="extend-row">
																<input type="date" value={extendDate} min={selectedTender.endDate || new Date().toISOString().split('T')[0]} onChange={e => setExtendDate(e.target.value)} />
																<button onClick={handleExtendTender}>Extend</button>
															</div>
														</div>
													)}

													<h4>Participants ({participants.length})</h4>
													<p className="participants-hint">Click a participant to view their full profile and reviews.</p>
													<div className="participants-list">
														{participants.map(p => (
															<div key={p.id}
																className="participant-card"
																onClick={() => handleParticipantClick(p.providerId)}>
																<div className="participant-card-body">
																	<strong>{p.providerName}</strong>
																	{p.providerPhone && <p className="participant-phone">📞 {p.providerPhone}</p>}
																	{p.vegCharge && <span className="bid-amount">Veg: ₹{p.vegCharge}/Plate</span>}
																	{p.nonVegCharge && <span className="bid-amount">Non Veg: ₹{p.nonVegCharge}/Plate</span>}
																	{p.banquetCharge && <span className="bid-amount">Banquet Charge: ₹{p.banquetCharge}/Function</span>}
																	{(!p.vegCharge && !p.nonVegCharge && !p.banquetCharge) &&  p.chargeAmount && <span className="bid-amount">₹{p.chargeAmount} / {p.chargeType}</span>}
																	{p.description && <p className="bid-description">{p.description}</p>}
																	<span className={`status-badge ${p.status?.toLowerCase()}`}>{p.status}</span>
																	<p className="rating">⭐ {p.providerRating?.toFixed(1) || '0.0'} ({p.providerReviewCount} reviews)</p>
																</div>
																{selectedTender.status === 'OPEN' && (
																	<button
																		type="button"
																		className="btn-assign"
																		onClick={(e) => handleAssign(p.id, e)}
																	>
																		Assign
																	</button>
																)}
															</div>
														))}
														{participants.length === 0 && <p className="empty-msg">No bids yet</p>}
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{activeTab === 'profile' && (
						<div className="cd-profile-view">
							<h2>My Profile</h2>
							<form onSubmit={handleProfileUpdate} className="profile-form">
								<div className="form-group">
									<label>Name</label>
									<input value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} />
								</div>
								<div className="form-group">
									<label>Phone</label>
									<input value={profile?.phone || ''} disabled />
								</div>
								<div className="form-group">
									<label>Email</label>
									<input type="email" value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} />
								</div>
								<div className="form-group">
									<label>City</label>
									<input value={profileForm.city} onChange={e => setProfileForm({ ...profileForm, city: e.target.value })} />
								</div>
								<div className="form-group">
									<label>State</label>
									<input value={profileForm.state} onChange={e => setProfileForm({ ...profileForm, state: e.target.value })} />
								</div>
								<button type="submit" className="btn-primary">Save Profile</button>
							</form>
						</div>
					)}
				</main>
			</div>

			{showCreateModal && (
				<CreateTenderModal
					loaderData={loaderData}
					onClose={() => setShowCreateModal(false)}
					onSuccess={fetchTenders}
				/>
			)}

			{previewPhotos && (
				<div className="cd-photo-preview-overlay" onClick={() => setPreviewPhotos(null)}>
					<div className="cd-photo-preview-modal" onClick={(e) => e.stopPropagation()}>
						<button
							type="button"
							className="cd-photo-preview-close"
							onClick={() => setPreviewPhotos(null)}
							aria-label="Close preview"
						>
							×
						</button>
						{previewPhotos.length > 1 && (
							<button
								type="button"
								className="cd-lightbox-nav cd-lightbox-nav--prev"
								onClick={() => setPreviewIndex(i => (i > 0 ? i - 1 : previewPhotos.length - 1))}
								aria-label="Previous photo"
							>
								&#8249;
							</button>
						)}
						<img src={previewPhotos[previewIndex]} alt={`Reference ${previewIndex + 1}`} />
						{previewPhotos.length > 1 && (
							<button
								type="button"
								className="cd-lightbox-nav cd-lightbox-nav--next"
								onClick={() => setPreviewIndex(i => (i < previewPhotos.length - 1 ? i + 1 : 0))}
								aria-label="Next photo"
							>
								&#8250;
							</button>
						)}
						<p className="cd-lightbox-counter">{previewIndex + 1} / {previewPhotos.length}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default CustomerDashboard;
